import { observer } from 'mobx-react';
import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { PageHeader } from '@components/page-header';
import { PageTabs } from '@components/page-tabs';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Spinner } from '@components/spinner';
import { ProductState, VendorDetailsSupportDto } from '@api/WalletClient';
import { useTabs } from '../../components/hooks/use-tabs';
import { useVendorDetailsStore } from './store';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Products', id: 'products' }
];

export const VendorContext = React.createContext<VendorDetailsSupportDto>(
    {} as VendorDetailsSupportDto
);

export const VendorActionsContext = React.createContext<{
    reloadVendor: () => Promise<void>;
}>({
    reloadVendor: async () => {}
});

export const VendorDetails = observer(() => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { tab, getPageLink, setPageTab } = useTabs(pageTabs);
    const { vendor } = useVendorDetailsStore();

    React.useEffect(() => {
        vendor.request([Number(vendorId)]);
    }, [vendorId]);

    React.useEffect(() => {
        setPageTab(pathname, 3);
    }, [pathname]);

    const reloadVendor = React.useCallback(async () => {
        await vendor.request([Number(vendorId)]);
    }, [vendorId, vendor]);

    const changePage = (page: number) => {
        navigate(getPageLink(page));
    };

    return (
        <>
            {vendor.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                error: () => (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {vendor.error?.message ||
                                'Failed to load vendor details'}
                        </AlertDescription>
                    </Alert>
                ),
                fetched: (vendorData) => (
                    <VendorContext.Provider value={vendorData!!}>
                        <VendorActionsContext.Provider value={{ reloadVendor }}>
                            <PageHeader
                                title={vendorData!!.name}
                                badges={
                                    <>
                                        <Badge
                                            variant={
                                                vendorData!!.shopState ===
                                                ProductState.PRODUCTION
                                                    ? 'success'
                                                    : 'muted'
                                            }
                                        >
                                            {vendorData!!.shopState}
                                        </Badge>
                                        <Badge variant="outline">
                                            {vendorData!!.source}
                                        </Badge>
                                    </>
                                }
                            />
                            <PageTabs
                                tabs={pageTabs}
                                value={tab}
                                onChange={changePage}
                            />
                            <div className="mt-6">
                                <Outlet />
                            </div>
                        </VendorActionsContext.Provider>
                    </VendorContext.Provider>
                )
            })}
        </>
    );
});
